import { useState, useEffect, useMemo } from 'react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '../ui'
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog'
import { Eye } from 'lucide-react'

export interface UserData {
  id: string
  username: string
  email: string
  role: string
  isActive: boolean
  isWhitelisted: boolean
  emailNotifications: boolean
}

export interface UserSettingsFormProps {
  user: UserData
  loading?: boolean
  showRoleSelector?: boolean
  allowedRoles?: string[]
  showPermissionsButton?: boolean
  showPasswordReset?: boolean
  showMfaReset?: boolean
  showStatusToggles?: boolean
  showEmailNotifications?: boolean

  // Callbacks
  onSave?: (formData: UserData) => Promise<void>
  onResetPassword?: (userId: string) => Promise<void>
  onResetMfa?: (userId: string) => Promise<void>
  onViewPermissions?: () => void

  // Loading states
  saveLoading?: boolean
  passwordResetLoading?: boolean
  mfaResetLoading?: boolean
}

const ALL_ROLES = [
  { value: 'TRADER', label: 'Trader' },
  { value: 'GROUP_OWNER', label: 'Group Owner' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'COMPLIANCE', label: 'Compliance' },
] as const

function getRoleTransitionMessage(previousRole: string, newRole: string): string | null {
  if (previousRole === 'GROUP_OWNER' && newRole === 'TRADER') {
    return 'User will be assigned to all companies in their group(s) and can be individually unassigned from companies.'
  }

  if (previousRole === 'TRADER' && newRole === 'GROUP_OWNER') {
    return 'User will be assigned to their group(s) with access to all companies in each group.'
  }

  if (previousRole === 'SUPER_ADMIN' && newRole === 'COMPLIANCE') {
    return 'User will become a compliance officer. Any group or company assignments will be removed.'
  }

  if (previousRole === 'COMPLIANCE' && newRole === 'SUPER_ADMIN') {
    return 'User will become a super admin with full MSB administrator access. Any group or company assignments will be removed.'
  }

  return null
}

export function UserSettingsForm({
  user,
  loading = false,
  showRoleSelector = true,
  allowedRoles,
  showPermissionsButton = true,
  showPasswordReset = true,
  showMfaReset = true,
  showStatusToggles = true,
  showEmailNotifications = true,
  onSave,
  onResetPassword,
  onResetMfa,
  onViewPermissions,
  saveLoading = false,
  passwordResetLoading = false,
  mfaResetLoading = false,
}: UserSettingsFormProps) {
  // Form state
  const [formData, setFormData] = useState<UserData>({
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    isWhitelisted: user.isWhitelisted,
    emailNotifications: user.emailNotifications,
  })

  // Original form data to track changes
  const [originalFormData, setOriginalFormData] = useState<UserData>({
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    isWhitelisted: user.isWhitelisted,
    emailNotifications: user.emailNotifications,
  })

  // Confirmation dialog states
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false)
  const [showResetMfaDialog, setShowResetMfaDialog] = useState(false)
  const [showRoleChangeDialog, setShowRoleChangeDialog] = useState(false)

  const roleOptions = useMemo(() => {
    if (!allowedRoles) {
      return ALL_ROLES
    }

    return ALL_ROLES.filter((role) => allowedRoles.includes(role.value))
  }, [allowedRoles])

  const pendingRoleChangeMessage = useMemo(() => {
    if (formData.role === originalFormData.role) {
      return null
    }

    return getRoleTransitionMessage(originalFormData.role, formData.role)
  }, [formData.role, originalFormData.role])

  // Update form data when user data changes
  useEffect(() => {
    const newFormData = {
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role,
      isActive: user.isActive,
      isWhitelisted: user.isWhitelisted,
      emailNotifications: user.emailNotifications,
    }
    setFormData(newFormData)
    setOriginalFormData(newFormData)
  }, [user])

  // Check if form has been modified
  const hasFormChanges = useMemo(() => {
    return (
      formData.username !== originalFormData.username ||
      formData.email !== originalFormData.email ||
      formData.role !== originalFormData.role ||
      formData.isActive !== originalFormData.isActive ||
      formData.isWhitelisted !== originalFormData.isWhitelisted ||
      formData.emailNotifications !== originalFormData.emailNotifications
    )
  }, [formData, originalFormData])

  const handleSave = async () => {
    if (pendingRoleChangeMessage) {
      setShowRoleChangeDialog(true)
      return
    }

    if (onSave) {
      await onSave(formData)
    }
  }

  const confirmRoleChange = async () => {
    if (onSave) {
      await onSave(formData)
    }
    setShowRoleChangeDialog(false)
  }

  const handleResetPassword = () => {
    setShowResetPasswordDialog(true)
  }

  const handleResetMfa = () => {
    setShowResetMfaDialog(true)
  }

  const confirmResetPassword = async () => {
    if (onResetPassword) {
      await onResetPassword(user.id)
    }
    setShowResetPasswordDialog(false)
  }

  const confirmResetMfa = async () => {
    if (onResetMfa) {
      await onResetMfa(user.id)
    }
    setShowResetMfaDialog(false)
  }

  const handleViewPermissions = () => {
    if (onViewPermissions) {
      onViewPermissions()
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="md:hidden text-xl font-semibold">User Settings</h2>
      <Card>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                disabled={saveLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={saveLoading}
                  className="flex-1"
                />
              </div>
            </div>

            {showEmailNotifications && (
              <div className="space-y-2">
                <Label>Email Notifications</Label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    checked={formData.emailNotifications}
                    onChange={(e) =>
                      setFormData({ ...formData, emailNotifications: e.target.checked })
                    }
                    disabled={saveLoading}
                    className="w-4 h-4 [accent-color:var(--color-main)]"
                  />
                  <Label htmlFor="emailNotifications" className="text-sm font-normal">
                    Enable email notifications
                  </Label>
                </div>
              </div>
            )}

            {showPasswordReset && (
              <div className="space-y-2">
                <Label>Password</Label>
                <Button
                  variant="main_outline"
                  onClick={handleResetPassword}
                  disabled={passwordResetLoading}
                  className="w-full md:w-auto rounded-md"
                >
                  {passwordResetLoading ? 'Resetting...' : 'Reset password'}
                </Button>
              </div>
            )}

            {showMfaReset && (
              <div className="space-y-2">
                <Label>MFA</Label>
                <Button
                  variant="main_outline"
                  onClick={handleResetMfa}
                  disabled={mfaResetLoading}
                  className="w-full md:w-auto rounded-md"
                >
                  {mfaResetLoading ? 'Resetting...' : 'Reset MFA'}
                </Button>
              </div>
            )}

            {showStatusToggles && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>User Status</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                      disabled={saveLoading}
                    />
                    <Label className="text-sm font-normal">
                      {formData.isActive ? 'Active' : 'Deactivated'}
                    </Label>
                  </div>
                </div>

                {formData.role === 'TRADER' && (
                  <div className="space-y-2">
                    <Label>Authorization Status</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={formData.isWhitelisted}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, isWhitelisted: checked })
                        }
                        disabled={saveLoading}
                      />
                      <Label className="text-sm font-normal">
                        {formData.isWhitelisted ? 'Authorized' : 'Not Authorized'}
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showRoleSelector && (
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <div className="flex gap-2 items-center">
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                    disabled={saveLoading}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showPermissionsButton && (
                    <Button
                      variant="main_outline"
                      size="sm"
                      className="rounded-md"
                      onClick={handleViewPermissions}
                    >
                      <Eye className="h-4 w-4" />
                      View permissions
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {onSave && (
            <div className="flex justify-end pt-4">
              <Button
                variant="main_action"
                onClick={handleSave}
                disabled={saveLoading || !hasFormChanges}
                className="px-8 rounded-md"
              >
                {saveLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={showResetPasswordDialog}
        onOpenChange={setShowResetPasswordDialog}
        title="Reset Password"
        description="Are you sure you want to reset this user's password? A password reset email will be sent to their email address."
        confirmText="Reset Password"
        onConfirm={confirmResetPassword}
        isLoading={passwordResetLoading}
        variant="destructive"
      />

      <ConfirmationDialog
        open={showResetMfaDialog}
        onOpenChange={setShowResetMfaDialog}
        title="Reset MFA"
        description="Are you sure you want to reset this user's MFA? They will need to set up MFA again on their next login."
        confirmText="Reset MFA"
        onConfirm={confirmResetMfa}
        isLoading={mfaResetLoading}
        variant="destructive"
      />
      <ConfirmationDialog
        open={showRoleChangeDialog}
        onOpenChange={setShowRoleChangeDialog}
        title="Confirm role change"
        description={pendingRoleChangeMessage ?? 'Are you sure you want to change this user type?'}
        confirmText="Change role"
        onConfirm={confirmRoleChange}
        isLoading={saveLoading}
        variant="destructive"
      />
    </div>
  )
}
